<template>
  <div class="question-panel__wrapper">
    <div class="question-panel__container">
      <div v-if="!showOnlyQuestionContent" class="question-panel__header">
        <p class="question-panel__step">
          {{ currentStepText }}
        </p>
        <p class="question-panel__description">
          {{ questionTitle }}
        </p>
      </div>
      <div class="question-panel__content">
        <template v-if="isQuestionType(freeResponse)">
          <div class="question-panel__content-wrapper">
            <PxTextarea
              v-model="innerValue.value!.text"
              :max-chars="textAreaLimit"
              :placeholder="
                $t('affiliateProgram.questions.freeTextPlaceholder')
              "
              :show-counter="true"
              resize="none"
              @focus="onTextareaFocus"
            />
          </div>
        </template>
        <template v-else-if="isQuestionType(singleSelect)">
          <PxRadioGroup
            v-model="innerValue.answers[0]"
            class="question-panel__content-wrapper"
          >
            <div
              v-for="(option, index) in question.answers"
              :key="`question-panel__option-${index}`"
              class="question-panel__answer"
            >
              <ElRadio :value="option.id">
                {{ option.value }}
              </ElRadio>
            </div>
          </PxRadioGroup>
        </template>
        <template v-else-if="isQuestionType(multiSelect)">
          <ElCheckboxGroup
            v-model="innerValue.answers"
            class="question-panel__content-wrapper"
          >
            <div
              v-for="(option, index) in question.answers"
              :key="`question-panel__option-${index}`"
              class="question-panel__answer"
            >
              <ElCheckbox :value="option.id">
                {{ option.value }}
              </ElCheckbox>
            </div>
          </ElCheckboxGroup>
        </template>
        <template v-else-if="isMoneyType">
          <div
            v-if="needsCurrencyRange"
            class="question-panel__content-wrapper question-panel__content-wrapper--two-column"
          >
            <div class="question-panel__range">
              <span class="question-panel__range-label">
                {{ $t("affiliateProgram.questions.rangeFrom") }}
              </span>
              <PxMoney
                v-model="innerValue.value!.min"
                data-testid="question-panel__range--min-value"
              />
            </div>
            <div class="question-panel__range">
              <span class="question-panel__range-label">
                {{ $t("affiliateProgram.questions.rangeTo") }}
              </span>
              <PxMoney
                v-model="innerValue.value!.max"
                data-testid="question-panel__range--max-value"
              />
            </div>
          </div>
          <div v-else class="question-panel__content-wrapper">
            <PxMoney v-model="innerValue.value!.value" />
          </div>
        </template>
        <template v-else-if="isQuestionType(numeric)">
          <div
            v-if="$user.isEntrepreneur()"
            class="question-panel__content-wrapper"
          >
            <PxNumeric v-model="innerValue.value!.value" />
          </div>
          <div
            v-if="$user.isSupporter()"
            class="question-panel__content-wrapper question-panel__content-wrapper--two-column"
          >
            <div class="question-panel__range">
              <span class="question-panel__range-label">
                {{ $t("affiliateProgram.questions.rangeFrom") }}
              </span>
              <PxNumeric v-model="innerValue.value!.min" />
            </div>
            <div class="question-panel__range">
              <span class="question-panel__range-label">
                {{ $t("affiliateProgram.questions.rangeTo") }}
              </span>
              <PxNumeric v-model="innerValue.value!.max" />
            </div>
          </div>
        </template>
        <template v-if="isQuestionType(dateResponse)">
          <div class="question-panel__content-wrapper">
            <PxDateSelect v-model="innerValue.value!.date" />
          </div>
        </template>
      </div>
    </div>
    <template v-if="$screen.mdUp && showNotice && !showOnlyQuestionContent">
      <div class="question-panel__info">
        <PxIcon
          :size="noticeIconSize"
          class="question-panel__info-icon"
          name="icon-information"
        />
        <span class="question-panel__info-title">
          {{ noticeText }}
        </span>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import QuestionPanelMixin from "@/mixins/question-panel.mixin";

export default defineComponent({
  name: "QuestionPanel",

  mixins: [QuestionPanelMixin],
});
</script>

<style lang="scss" scoped>
.question-panel__container {
  margin: 0 auto;

  @include breakpoint-up(md) {
    width: 611px;
    background-color: $white;
    border: solid 1px $pale-grey;
    border-radius: 2px;
    box-shadow: 0 2px 11px 0 $black-5;
  }

  @include breakpoint-down(sm) {
    background-color: $alabaster;
  }
}

.question-panel__header {
  min-height: 98px;
  padding: 18px;

  @include breakpoint-up(sm) {
    padding: 15px 60px;
    background-image: linear-gradient(
      to bottom,
      $white,
      $white 50%,
      $alabaster
    );
    border-bottom: 1px solid $athens-gray;
  }
}

.question-panel__content {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding: 0 15px 120px;

  @include breakpoint-up(md) {
    padding: 35px 80px;
  }

  :deep(.el-input__inner[type="number"]::-webkit-inner-spin-button),
  :deep(.el-input__inner[type="number"]::-webkit-outer-spin-button) {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }
}

.question-panel__content-wrapper {
  display: grid;
  grid-row-gap: 10px;
  width: 100%;

  @include breakpoint-up(md) {
    width: 460px;
  }

  &.question-panel__content-wrapper--two-column {
    @include breakpoint-up(md) {
      grid-template-columns: repeat(2, auto);
    }
  }

  :deep(.el-textarea__inner) {
    width: 100%;
    height: 194px;
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.71;
    color: $gun-powder;
    resize: none;
    border: 1px solid $mischka;
    border-radius: 2px;
    box-shadow: inset 0 0 5px 0 rgba(0, 0, 0, 0.07);
  }

  :deep(.date-select__wrapper) {
    justify-content: space-evenly;

    @include breakpoint-up(md) {
      justify-content: center;
    }
  }
}

.question-panel__step {
  margin-bottom: 11px;
  font-size: 15px;
  color: $manatee;
  text-align: center;
}

.question-panel__description {
  @include grotesk(semiBold);

  margin: 0;
  font-size: 17px;
  line-height: 30px;
  text-align: center;
}

.pxNumeric,
.pxMoney {
  width: 100%;
  background-color: $white;

  border-radius: 2px;
  box-shadow: inset 0 0 5px 0 rgba(0, 0, 0, 0.07);

  :deep(.el-input__prefix) {
    border: none;
  }

  :deep(.el-input__prefix::after) {
    right: -11px;
    top: 0;
  }

  :deep(.el-input__prefix > span > span) {
    color: rgba(139, 143, 161, 0.6);
  }

  :deep(.el-input__inner) {
    font-size: 14px;
    color: $ebony-clay;
  }

  :deep(.el-input__inner::placeholder) {
    font-size: 14px;
    color: $manatee;
  }
}

.question-panel__range {
  flex: 1 0 100%;
  margin: 5px 0;

  @include breakpoint-up(md) {
    flex: 1 0 40%;
    margin: 5px;
  }

  &-label {
    @include grotesk(bold);

    font-size: 12px;
    color: $manatee;
  }
}

.question-panel__answer {
  flex: 1 0 100%;
  min-height: 50px;

  cursor: pointer;
  background-color: $white;
  border: 1px solid $pale-grey;
  border-radius: 4px;
  box-shadow: 0 2px 11px 0 $black-5;

  @include breakpoint-up(md) {
    min-height: 38px;
    background-image: linear-gradient(to bottom, $white, #f6f6f6 83%),
      linear-gradient(to bottom, $ebony-clay-10, $ebony-clay-10 94%);
    box-shadow: none;
  }
}

.el-radio-group.question-panel__content-wrapper {
  width: auto;
  min-width: 330px;
  margin: 0 auto;
}

.question-panel__answer :deep(.el-radio) {
  display: grid;
  grid-template-areas: "label input";
  grid-template-columns: auto 23px;
  grid-column-gap: 10px;
  width: 100%;
  height: 100%;
  padding: 5px 10px 5px 14px;
  margin: 0;

  @include breakpoint-down(sm) {
    padding: 12px 10px 12px 14px;
  }

  span {
    @include grotesk(medium);

    padding: 0;
    font-size: 15px;
    line-height: 24px;
    color: $ebony-clay;
  }

  .el-radio__original {
    cursor: pointer;
  }
}

.question-panel__answer :deep(.el-radio__label) {
  grid-area: label;
  white-space: normal;
}

.question-panel__answer :deep(.el-radio__input) {
  grid-area: input;
  width: 23px;
  height: 23px;
  margin-top: 1px;

  .el-radio__inner {
    width: 22px;
    height: 22px;
    background-color: $white;
    border: solid 1px $ebony-clay-10;
    border-radius: 50%;
    box-shadow: inset 0 0 4px 0 $black-10;

    &::after {
      top: 0;
      left: 0;
      width: 20px;
      height: 20px;
      background-color: transparent;
      background-image: url("#{$assetsPath}/img/icons/icon-check-white-circle.svg");
      background-position: center;
      border: none;
      box-shadow: none;
    }
  }

  &.is-checked .el-radio__inner {
    background: none;
    border-color: transparent;
    box-shadow: none;
  }
}

.question-panel__answer :deep(.el-checkbox) {
  display: grid;
  grid-template-areas: "label input";
  grid-template-columns: auto 23px;
  grid-column-gap: 10px;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 5px 10px 5px 14px;
  margin: 0;

  @include breakpoint-down(sm) {
    padding: 12px 10px 12px 14px;
  }

  span {
    @include grotesk(semiBold);

    padding: 0;
    font-size: 15px;
    line-height: 24px;
    color: $ebony-clay;
  }

  .el-checkbox__input span {
    width: 20px;
    height: 20px;
  }

  .is-checked span {
    background-color: #5696f0;
    border-color: #5696f0;
  }
}

.question-panel__answer :deep(.el-checkbox__label) {
  grid-area: label;
  white-space: normal;
}

.question-panel__answer :deep(.el-checkbox__input) {
  display: flex;
  grid-area: input;
  align-items: center;
  width: 23px;
  height: 23px;
  margin-top: 1px;

  .el-checkbox__input__inner {
    width: 22px;
    height: 22px;
    background-color: $white;

    &::after {
      width: 22px;
      height: 22px;
      background-image: url("#{$assetsPath}/img/icons/icon-check-white-square.svg");
      background-position: center;
      opacity: 0;
      transition: opacity 0.15ms;
    }
  }

  .el-checkbox__input.is-checked .el-checkbox__inner::after {
    opacity: 1;
    transition: opacity 0.15ms;
  }
}

.question-panel__info {
  display: flex;
  width: 611px;
  padding: 16px 30px;
  margin: 0 auto;

  &-icon {
    margin-top: 2px;
  }

  &-title {
    margin-left: 6px;
    font-size: 13px;
    font-style: italic;
    line-height: 18px;
  }
}
</style>
